import { useState, useEffect } from 'react';
import { getPendingLeavesDetails, getApprovedLeavesDetails, getDeclinedLeavesDetails, getManagerPendingLeaves } from 'api/apiHelper';
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab"; 
import AssignMaxLeavesForm from 'features/leaves/AssignMaxLeavesForm';
import RegisterLeaveTypeForm from 'features/leaves/RegisterLeaveTypeForm';
import AssignedLeavesTable from 'features/leaves/AssignedLeavesTable';
import { useIsManager, usePermissions } from 'app/hooks';
import LeaveTypesTable from "features/leaves/LeavesTypesTable";
import { getManagerApprovedLeaves, getManagerDeclinedLeaves } from 'api/apiHelper';
import UnifiedLeavesTable from "features/leaves/UnifiedLeavesTable";

export interface LeaveDetailsType {
  id: number;
  employee_id: number;
  employee_first_name: string;
  employee_last_name: string;
  start_date: string;   
  end_date: string; 
  leave_status: "P" | "A" | "D"| "MA" | "MD" | "CA" | "CR";
  reason: string | null; 
  comments: string | null;
  leave_info: number;
  manager_approved_by?: string | null;
  approved_by?: string | null;
  leave_type_description: string;
  requested_at: string;
}

interface EmployeeLeavesType {
  setShowManage: React.Dispatch<React.SetStateAction<boolean>>;
}

const EmployeesLeaves = ({setShowManage}: EmployeeLeavesType) => {
  const [pendingLeaves, setPendingLeaves] = useState<LeaveDetailsType[]>([]);
  const [approvedLeaves, setApprovedLeaves] = useState<LeaveDetailsType[]>([]);
  const [declinedLeaves, setDeclinedLeaves] = useState<LeaveDetailsType[]>([]);
  const [managerPendingLeaves, setManagerPendingLeaves] = useState<LeaveDetailsType[]>([]);
  const [managerApprovedLeaves, setManagerApprovedLeaves] = useState<LeaveDetailsType[]>([]);
  const [managerDeclinedLeaves, setManagerDeclinedLeaves] = useState<LeaveDetailsType[]>([]);
  const permissions = usePermissions() || [];
  const isManager = useIsManager() || false;

  const fetchManagerPendingLeaves = async () => {
    if (isManager) {
      try {      
        const response = await getManagerPendingLeaves();
        setManagerPendingLeaves(response.data);
      } catch (error) {
        console.log("Error fetching manager pending leaves:", error);
      }
    }
  };
  
  const fetchManagerApprovedLeaves = async () => {
    if (isManager) {
      try {
        const response = await getManagerApprovedLeaves();
        setManagerApprovedLeaves(response.data);
      } catch (error) {
        console.log("Error fetching manager approved leaves:", error);
      }
    }
  };

  const fetchManagerDeclinedLeaves = async () => {
    if (isManager) {
      try {
        const response = await getManagerDeclinedLeaves();
        setManagerDeclinedLeaves(response.data);
      } catch (error) {
        console.log("Error fetching manager declined leaves:", error);
      }
    }
  };

  const fetchPendingLeaves = async () => {
    if (permissions.includes('leaves.view_employeeleavesdetails')) {
      try {
        const response = await getPendingLeavesDetails();
        setPendingLeaves(response.data);
      } catch (error) {
        console.log("Error fetching pending leaves:", error);
      }
    }
  };

  const fetchApprovedLeaves = async () => {
    if (permissions.includes('leaves.view_employeeleavesdetails')) {
      try {
        const response = await getApprovedLeavesDetails();
        setApprovedLeaves(response.data);
      } catch (error) {
        console.log("Error fetching approved leaves:", error);
      }
    }
  };

  const fetchDeclinedLeaves = async () => {
    if (permissions.includes('leaves.view_employeeleavesdetails')) {
      try {
        const response = await getDeclinedLeavesDetails();
        setDeclinedLeaves(response.data);
      } catch (error) {
        console.log("Error fetching declined leaves:", error);
      }
    }
  };

  useEffect(() => {
    fetchPendingLeaves();
    fetchApprovedLeaves();
    fetchDeclinedLeaves();
    fetchManagerPendingLeaves();
    fetchManagerApprovedLeaves();
    fetchManagerDeclinedLeaves();
  }, []);

  const requestLeaveButton = (
    <button
      className="bg-brand-500 flex gap-2 justify-center items-center uppercase hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-lg px-4 py-3 text-[0.8rem] font-bold text-white"
      onClick={() => setShowManage(false)}
    >
      <i className="fas fa-calendar-plus text-[0.8rem]"></i>
      <span>Request Leave</span>
    </button>
  );

  return (
    <div className="mt-5 relative">
      <TabsContainer headerActions={requestLeaveButton}>
        {/* All Assignments Tab */}
        {permissions.includes("leaves.view_employeeleaveinfo") && (
          <Tab isActive={false} title="Overview" key={5}>
            <AssignedLeavesTable />
          </Tab>
        )}
        {/* Manager Requests Tab - Unified */}
        {isManager && (
          <Tab isActive={true} title="Manage Requests" key={0}>
            <UnifiedLeavesTable
              pendingData={managerPendingLeaves}
              approvedData={managerApprovedLeaves}
              declinedData={managerDeclinedLeaves}
              isManager={true}
            />
          </Tab>
        )}

        {/* HR/Admin Requests Tab - Unified */}
        {permissions.includes("leaves.view_employeeleavesdetails") && (
          <Tab isActive={!isManager} title="All Requests" key={1}>
            <UnifiedLeavesTable
              pendingData={pendingLeaves}
              approvedData={approvedLeaves}
              declinedData={declinedLeaves}
              isManager={false}
            />
          </Tab>
        )}

        {/* Register Type Tab */}
        {permissions.includes("leaves.add_employeeleavetype") && (
          <Tab isActive={false} title="Register Type" key={2}>
            <RegisterLeaveTypeForm />
          </Tab>
        )}

        {/* All Types Tab */}
        {/* {permissions.includes("leaves.view_employeeleavetype") && (
          <Tab isActive={false} title="All Types" key={3}>
            <LeaveTypesTable />
          </Tab>
        )} */}

        {/* Assign Tab */}
        {permissions.includes("leaves.add_employeeleaveinfo") && (
          <Tab isActive={false} title="Assign" key={4}>
            <AssignMaxLeavesForm />
          </Tab>
        )}

      </TabsContainer>
    </div>
  );
};

export default EmployeesLeaves;